/**
 * Canonical CargoNova AI agent roster.
 * Used for (a) the ItemList / SoftwareApplication JSON-LD on /digital-workers
 * and (b) the screen-reader agent index rendered inside the showcase.
 * Anchor ids must stay in sync with the DOM ids on the page.
 */
import type { SeoLang } from "./seo";

export type AgentEntry = {
  /** DOM anchor id, e.g. #novaquote */
  id: string;
  name: string;
  role: { en: string; zh: string; th: string };
  duty: { en: string; zh: string; th: string };
};

export const AGENTS: AgentEntry[] = [
  {
    id: "novarate",
    name: "NovaRate",
    role: {
      en: "Rate management agent",
      zh: "运价管理智能体",
      th: "เอเจนต์บริหารค่าระวาง",
    },
    duty: {
      en: "Collects and cleans carrier tariffs, then keeps a single live rate pool for every trade lane.",
      zh: "自动采集与清洗船司运价，维护一个实时统一的运价池。",
      th: "รวบรวมและจัดระเบียบค่าระวางจากสายเรือ ให้เป็นฐานราคาเดียวที่อัปเดตแบบเรียลไทม์",
    },
  },
  {
    id: "novasales",
    name: "NovaSales",
    role: {
      en: "Sales agent",
      zh: "销售智能体",
      th: "เอเจนต์ฝ่ายขาย",
    },
    duty: {
      en: "Reads inbound enquiries, qualifies leads and follows up with customers around the clock.",
      zh: "读取客户询盘、判断商机质量并 7×24 小时跟进客户。",
      th: "อ่านคำถามจากลูกค้า คัดกรองโอกาสการขาย และติดตามลูกค้าตลอด 24 ชั่วโมง",
    },
  },
  {
    id: "novaquote",
    name: "NovaQuote",
    role: {
      en: "AI freight quotation agent",
      zh: "智能报价智能体",
      th: "เอเจนต์ใบเสนอราคาอัจฉริยะ",
    },
    duty: {
      en: "Turns an enquiry into a priced, margin-checked quotation in minutes — AI freight quotation end to end.",
      zh: "把一封询盘变成含成本与毛利校验的报价单，实现端到端 AI 智能报价。",
      th: "เปลี่ยนคำขอราคาเป็นใบเสนอราคาที่ตรวจสอบต้นทุนและกำไรแล้วภายในไม่กี่นาที",
    },
  },
  {
    id: "novabook",
    name: "NovaBook",
    role: {
      en: "Booking agent",
      zh: "订舱智能体",
      th: "เอเจนต์จองระวาง",
    },
    duty: {
      en: "Places bookings with carriers and platforms, then tracks confirmations and schedule changes.",
      zh: "向船司与订舱平台下单，并持续跟踪舱位确认与船期变更。",
      th: "ส่งคำสั่งจองไปยังสายเรือและแพลตฟอร์ม พร้อมติดตามการยืนยันและตารางเรือ",
    },
  },
  {
    id: "novadocs",
    name: "NovaDocs",
    role: {
      en: "Documentation agent",
      zh: "单证智能体",
      th: "เอเจนต์งานเอกสาร",
    },
    duty: {
      en: "Reads B/L, invoices and packing lists with OCR, then drafts and cross-checks shipping documents.",
      zh: "用 OCR 识别提单、发票与箱单，自动生成并交叉校验运输单证。",
      th: "อ่าน B/L ใบแจ้งหนี้ และแพ็กกิ้งลิสต์ด้วย OCR แล้วจัดทำและตรวจทานเอกสารขนส่ง",
    },
  },
  {
    id: "novacustoms",
    name: "NovaCustoms",
    role: {
      en: "Customs compliance agent",
      zh: "关务合规智能体",
      th: "เอเจนต์พิธีการศุลกากร",
    },
    duty: {
      en: "Classifies HS codes, screens sanctions lists and prepares customs filings per country rules.",
      zh: "归类 HS 编码、筛查管制名单，并按各国规则准备报关申报。",
      th: "จัดพิกัด HS ตรวจรายชื่อควบคุม และเตรียมการยื่นพิธีการตามกฎแต่ละประเทศ",
    },
  },
  {
    id: "novafinance",
    name: "NovaFinance",
    role: {
      en: "Settlement & audit agent",
      zh: "结算稽核智能体",
      th: "เอเจนต์การเงินและตรวจสอบ",
    },
    duty: {
      en: "Matches carrier invoices against quotes and costs, flagging every variance before payment.",
      zh: "将船司账单与报价、成本自动对账，付款前标出每一笔差异。",
      th: "กระทบยอดใบแจ้งหนี้กับใบเสนอราคาและต้นทุน พร้อมแจ้งส่วนต่างก่อนชำระเงิน",
    },
  },
  {
    id: "novarisk",
    name: "NovaRisk",
    role: {
      en: "Risk control agent",
      zh: "风控智能体",
      th: "เอเจนต์บริหารความเสี่ยง",
    },
    duty: {
      en: "Watches credit, detention and delay exposure and alerts the team before losses land.",
      zh: "监控信用、滞箱与延误风险，在损失发生前提醒团队。",
      th: "เฝ้าติดตามความเสี่ยงด้านเครดิต ค่าตู้ตกค้าง และความล่าช้า พร้อมแจ้งเตือนล่วงหน้า",
    },
  },
];

export const AGENT_INDEX_INTRO: Record<SeoLang, string> = {
  en: "CargoNova runs eight freight forwarding AI agents on one See–Calculate–Write loop: they see the data, calculate the answer, and write it back into the system of record. Together they work as freight rate management software and freight quoting software inside one digital freight forwarder platform — AI for freight forwarders, from enquiry to settlement.",
  zh: "CargoNova 由 8 个货代 AI 智能体组成，共用同一套「看-算-写」闭环：看数据、算结论、把结果写回业务系统。它们共同构成货代报价系统与运价管理系统，是数字货代平台上的 AI 数字员工，覆盖 AI 货代从询盘到结算的全流程，服务智能货运代理业务。",
  th: "CargoNova มี AI Agent สำหรับงานฟอร์เวิร์ดเดอร์ 8 ตัว ทำงานบนวงจร ดู–คำนวณ–เขียน เดียวกัน คือ อ่านข้อมูล คำนวณคำตอบ และเขียนกลับเข้าระบบงาน ทำหน้าที่เป็นระบบบริหารค่าระวางและระบบทำใบเสนอราคาบนแพลตฟอร์ม freight forwarder ดิจิทัลเดียว ครบตั้งแต่คำขอราคาจนถึงการชำระเงิน",
};


const PATH: Record<SeoLang, string> = {
  en: "https://cargonova.ai/digital-workers",
  zh: "https://cargonova.ai/zh/digital-workers",
  th: "https://cargonova.ai/th/digital-workers",
};

/** ItemList JSON-LD naming each agent as a SoftwareApplication with its anchor URL. */
export const agentsItemListJsonLd = (lang: SeoLang) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name:
    lang === "zh"
      ? "CargoNova 货代 AI 智能体（AI 数字员工）"
      : lang === "th"
        ? "AI Agent สำหรับฟอร์เวิร์ดเดอร์ของ CargoNova"
        : "CargoNova freight forwarding AI agents",
  itemListElement: AGENTS.map((a, i) => ({
    "@type": "ListItem",
    position: i + 1,
    item: {
      "@type": "SoftwareApplication",
      name: a.name,
      alternateName: a.role[lang],
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      url: `${PATH[lang]}#${a.id}`,
      description: a.duty[lang],
      isPartOf: { "@type": "SoftwareApplication", name: "CargoNova" },
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    },
  })),
});
